import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Clock,
  Download,
  Hourglass,
  ListChecks,
  Lock,
  MessageCircle,
  Pencil,
  Printer,
  Send,
  Sparkles,
  Upload,
  Vote as VoteIcon,
} from "lucide-react";
import {
  api,
  ApiError,
  type BoardVotingDetail,
  type CommentRow,
  type CommentSummaryEnvelope,
  type Project,
  type ProjectDetail,
  type Requirement,
} from "../api";
import { extractUrls } from "../lib/extractUrls";
import { LinkPreviewCard } from "../components/LinkPreviewCard";
import { hasAnyRole, useAuth } from "../auth";
import {
  AnomalyPanel,
  ConditionsExplanationPanel,
  ContractorsPanel,
  FeesPanel,
  IndemnityPanel,
  ProceduresAcknowledgementPanel,
  PreflightPanel,
  RulesAcknowledgementPanel,
  RiserShutdownPanel,
  TimelineCard,
} from "../components/CompliancePanels";
import { ScopeRefineButton } from "../components/ScopeRefine";
import { Button, Card, Field, PageHeader, StatusBadge, cx } from "../ui";

// Document type codes that are auto-fulfilled (click-wrap or
// owner-policy lookup) and shouldn't show up as upload targets in
// the file picker or URL-import flow.
const UPLOAD_EXCLUDED_DOC_CODES = new Set([
  "scope_of_work",
  "indemnity_agreement",
  "rules_acknowledgement",
  "procedures_acknowledgement",
  "owner_liability_coi",
]);

const VOTE_OPTIONS = [
  { value: "approve", label: "Approve" },
  { value: "conditionally_approve", label: "Conditionally Approve" },
  { value: "reject", label: "Reject" },
  { value: "request_revisions", label: "Request Revisions" },
  { value: "abstain", label: "Abstain" },
  { value: "recuse", label: "Recuse" },
] as const;

export function ProjectPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const startInEdit = searchParams.get("edit") === "1";
  const { state } = useAuth();
  const nav = useNavigate();

  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [comments, setComments] = useState<CommentRow[] | null>(null);
  const [voting, setVoting] = useState<BoardVotingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      setDetail(await api.get<ProjectDetail>(`/api/projects/${id}`));
      setComments(
        (await api.get<{ data: CommentRow[] }>(`/api/projects/${id}/comments`))
          .data,
      );
      try {
        const v = await api.get<{
          data: BoardVotingDetail | { state: string };
        }>(`/api/projects/${id}/board/voting`);
        setVoting(
          "approvalStepId" in v.data ? (v.data as BoardVotingDetail) : null,
        );
      } catch {
        setVoting(null);
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? `Load failed (${err.status})` : "Load failed",
      );
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!detail) {
    return (
      <Card>
        {error ? (
          <div className="alert-error">{error}</div>
        ) : (
          <p className="text-ink-500">Loading…</p>
        )}
      </Card>
    );
  }
  if (state.status !== "authenticated") return null;

  const me = state.me;
  const project = detail.data;
  const isStaff = hasAnyRole(me, ["property_manager", "admin"]);
  const isBoard = hasAnyRole(me, ["board_member"]);
  // Server tells us authoritatively whether the principal owns this unit.
  // A user can hold both unit_owner and board_member roles; we used to
  // derive isOwner from absence of staff/board roles, which incorrectly
  // locked board-member-owners out of owner-side actions on their own
  // project.
  const isOwnerOfUnit = detail.isOwnerOfUnit;
  const isRecusedHere = detail.isRecusedHere;

  return (
    <div>
      <PageHeader
        back={
          <button
            onClick={() => nav(-1)}
            className="text-white/70 hover:text-white text-[12.5px] inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        }
        title={project.title}
        subtitle={
          <>
            <div>
              Created {new Date(project.createdAt).toLocaleDateString()}
              {project.updatedAt &&
                new Date(project.updatedAt).getTime() -
                  new Date(project.createdAt).getTime() >
                  60_000 &&
                ` · modified ${new Date(project.updatedAt).toLocaleDateString()}`}
              {project.submittedAt &&
                ` · submitted ${new Date(project.submittedAt).toLocaleDateString()}`}
              {project.approvedAt &&
                ` · approved ${new Date(project.approvedAt).toLocaleDateString()}`}
            </div>
            {detail.owners.length > 0 && (
              <div className="text-[12px] text-white/70 mt-0.5">
                {detail.owners.length === 1 ? "Owner: " : "Owners: "}
                {detail.owners
                  .map((o) =>
                    o.ownershipType === "primary"
                      ? o.name
                      : `${o.name} (${o.ownershipType.replace("_", "-")})`,
                  )
                  .join(", ")}
              </div>
            )}
          </>
        }
        actions={
          <div className="inline-flex items-center gap-2">
            {detail.unreadCommentCount > 0 && (
              <a
                href="#section-comments"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-status-rejected-bg text-status-rejected-fg no-underline hover:no-underline whitespace-nowrap"
                aria-label={`${detail.unreadCommentCount} new comments`}
              >
                <MessageCircle size={12} />
                {detail.unreadCommentCount} new
              </a>
            )}
            <a
              href={`/api/projects/${project.id}/bundle.html`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-sand-100 text-ink-700 hover:bg-sand-200 no-underline hover:no-underline whitespace-nowrap"
              aria-label="Print or download a bundle of this project's information"
              title="Opens a printable summary of this project. Use the browser's print dialog to save as PDF."
            >
              <Printer size={12} />
              Print bundle
            </a>
            <a
              href={`/api/projects/${project.id}/bundle.zip`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-sand-100 text-ink-700 hover:bg-sand-200 no-underline hover:no-underline whitespace-nowrap"
              aria-label="Download project bundle as a zip with attached documents"
              title="Downloads a zip with the printable HTML, a manifest, and the project's clean-scanned documents."
            >
              <Download size={12} />
              Bundle (.zip)
            </a>
            {detail.voting.decision ? (
              <StatusBadge status={detail.voting.decision} />
            ) : null}
          </div>
        }
      />

      <StatusTimelineCard detail={detail} />

      {detail.whiteGlove && (
        <Card className="!bg-status-review-bg !border-status-review-fg/20">
          <div className="text-[12.5px] text-status-review-fg leading-snug">
            <strong>White-glove submission.</strong>{" "}
            Created by{" "}
            <strong>{detail.whiteGlove.actorName ?? "Property Management"}</strong>{" "}
            on behalf of{" "}
            <strong>
              {detail.whiteGlove.onBehalfOfName ?? "the unit owner"}
            </strong>
            . The owner controls all submission gates (insurance, indemnity,
            click-wraps) and can edit or submit from their account.
          </div>
        </Card>
      )}

      <NextActionBanner
        detail={detail}
        isOwnerOfUnit={isOwnerOfUnit}
        isStaff={isStaff}
        isBoard={isBoard}
        isRecusedHere={isRecusedHere}
      />

      {isStaff && <AnomalyPanel projectId={project.id} />}

      {(detail.statusCode === "conditionally_approved" ||
        detail.statusCode === "scheduled" ||
        detail.statusCode === "in_progress" ||
        detail.statusCode === "inspection_required" ||
        detail.statusCode === "completed") && (
        <ConditionsExplanationPanel projectId={project.id} />
      )}

      <div id="section-scope">
        {detail.isEditableByOwner ? (
          <EditDraftCard
            detail={detail}
            onChange={reload}
            startInEdit={startInEdit}
          />
        ) : (
          <Card>
            <h3 className="text-ink-800 mb-2">Scope</h3>
            <p className="whitespace-pre-wrap text-ink-700">
              {project.scopeOfWork}
            </p>
          </Card>
        )}

        <KitchenCabinetSpecsCard project={project} />
      </div>

      <TimelineCard deadlines={detail.deadlines} />

      {detail.isEditableByOwner && <PreflightPanel projectId={project.id} />}

      <RequirementsCard
        detail={detail}
        onReload={reload}
        projectId={project.id}
      />

      <div id="section-rules-ack">
        <RulesAcknowledgementPanel
          projectId={project.id}
          isOwner={isOwnerOfUnit}
          onChange={reload}
        />
      </div>

      <div id="section-procedures-ack">
        <ProceduresAcknowledgementPanel
          projectId={project.id}
          isOwner={isOwnerOfUnit}
          onChange={reload}
        />
      </div>

      <div id="section-indemnity">
        <IndemnityPanel
          projectId={project.id}
          isOwner={isOwnerOfUnit}
          onChange={reload}
        />
      </div>

      <ContractorsPanel projectId={project.id} />

      <FeesPanel projectId={project.id} isStaff={isStaff} onChange={reload} />

      <div id="section-riser-shutdown">
        <RiserShutdownPanel
          projectId={project.id}
          buildingId={project.buildingId}
          unitId={project.unitId}
          isOwner={isOwnerOfUnit}
          isStaff={isStaff}
          onChange={reload}
        />
      </div>

      <VotingCard
        voting={voting}
        summary={detail.voting}
        isStaff={isStaff}
        isBoard={isBoard}
        isRecusedHere={isRecusedHere}
        projectId={project.id}
        onChange={reload}
      />

      <div id="section-comments">
      <CommentsCard
        projectId={project.id}
        comments={comments}
        unreadCount={detail.unreadCommentCount}
        onMarkRead={() => void markCommentsRead(project.id, reload)}
        // Board members who own this unit cannot post staff-internal
        // comments here; the server enforces it too.
        canPostInternal={isStaff || (isBoard && !isRecusedHere)}
        // Owners get the URL-import action on PDF / image link
        // previews. Staff don't; only the unit owner can ingest a
        // URL into their own project documents (server enforces).
        importCandidates={
          isOwnerOfUnit
            ? detail.requirements.filter(
                (r) =>
                  r.requiredFrom === "owner" &&
                  !UPLOAD_EXCLUDED_DOC_CODES.has(r.documentTypeCode),
              )
            : []
        }
        onChange={reload}
      />
      </div>

      <OwnerActions detail={detail} onChange={reload} />

      {isStaff && (
        <StaffActions projectId={project.id} onChange={reload} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow timeline. Renders the chain of statuses this project will
// pass through with the active step highlighted, "avg N days" hints
// per step, and concrete dates where we have them. Replaces the
// flat StatusBadge with something that orients first-time owners.
// ---------------------------------------------------------------------------
type TimelineStep = {
  /** Status codes that occupy this step (some statuses share a step). */
  matches: ReadonlyArray<string>;
  label: string;
  hint?: string;
};

const TIMELINE_A: TimelineStep[] = [
  { matches: ["draft"], label: "Draft" },
  { matches: ["submitted"], label: "Submitted", hint: "avg 1d" },
  {
    matches: ["under_management_review"],
    label: "Management notification",
    hint: "avg 1–2d",
  },
  {
    matches: ["approved", "conditionally_approved", "scheduled", "in_progress"],
    label: "Approved",
  },
  { matches: ["completed", "closed"], label: "Closed" },
];
const TIMELINE_BC: TimelineStep[] = [
  { matches: ["draft"], label: "Draft" },
  { matches: ["submitted"], label: "Submitted", hint: "avg 1d" },
  {
    matches: ["under_management_review"],
    label: "Management review",
    hint: "avg 3d",
  },
  {
    matches: ["under_board_review"],
    label: "Board vote",
    hint: "14d window",
  },
  {
    matches: ["approved", "conditionally_approved", "scheduled"],
    label: "Approved",
  },
  {
    matches: ["in_progress", "inspection_required"],
    label: "In progress",
  },
  { matches: ["completed", "closed"], label: "Closed" },
];

// ---------------------------------------------------------------------------
// Kitchen cabinet-replacement specs (read-only)
//
// When the owner ticked "Replacing kitchen cabinets" in the wizard,
// the wizard collected structured descriptions + optional links for
// countertop, faucet, and sink. Server-side submission won't accept
// the project without the three descriptions (see
// kitchen_cabinet_details_required in projects.ts). This card
// surfaces what was captured so reviewers see it inline with Scope
// rather than having to dig into metadata. Hidden when the project
// isn't kitchen or the flag isn't set.
// ---------------------------------------------------------------------------
function KitchenCabinetSpecsCard({ project }: { project: Project }) {
  const md = (project as Project & { metadata?: unknown }).metadata;
  const kitchen =
    md &&
    typeof md === "object" &&
    "typeDetails" in md &&
    md.typeDetails &&
    typeof md.typeDetails === "object" &&
    "kitchen" in md.typeDetails &&
    md.typeDetails.kitchen &&
    typeof md.typeDetails.kitchen === "object"
      ? (md.typeDetails.kitchen as Record<string, unknown>)
      : null;
  if (!kitchen || kitchen.replacingCabinets !== true) return null;

  const rows: Array<{
    label: string;
    description: string;
    link: string;
  }> = [
    {
      label: "Countertop",
      description: stringOr(kitchen.countertopDescription),
      link: stringOr(kitchen.countertopLink),
    },
    {
      label: "Faucet",
      description: stringOr(kitchen.faucetDescription),
      link: stringOr(kitchen.faucetLink),
    },
    {
      label: "Sink",
      description: stringOr(kitchen.sinkDescription),
      link: stringOr(kitchen.sinkLink),
    },
  ];

  return (
    <Card className="mt-3">
      <h3 className="text-ink-800 mb-2">Cabinet replacement specs</h3>
      <p className="text-[12.5px] text-ink-500 m-0 mb-3">
        Plumbing scope is required for cabinet-replacement kitchens
        (plumber's COI and license on file).
      </p>
      <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-[13px] m-0">
        {rows.map((r) => (
          <Fragment key={r.label}>
            <dt className="text-[11.5px] uppercase tracking-wide text-ink-400">
              {r.label}
            </dt>
            <dd className="text-ink-700 m-0">
              {r.description || (
                <span className="text-ink-400">(not provided)</span>
              )}
              {r.link && (
                <a
                  href={r.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-brand-700 hover:text-brand-800 text-[12px]"
                >
                  link ↗
                </a>
              )}
            </dd>
          </Fragment>
        ))}
      </dl>
    </Card>
  );
}

function stringOr(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function StatusTimelineCard({ detail }: { detail: ProjectDetail }) {
  const cat = detail.categoryCode ?? "B";
  const steps = cat === "A" ? TIMELINE_A : TIMELINE_BC;
  const status = detail.statusCode;

  // Special-case "rejected" — it short-circuits the timeline.
  const rejected = status === "rejected";
  let activeIdx = steps.findIndex((s) => s.matches.includes(status));
  if (rejected) activeIdx = -1; // none "current"

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-ink-800 m-0 text-[14px]">Workflow</h3>
        {rejected && (
          <span className="badge bg-status-rejected-bg text-status-rejected-fg">
            rejected
          </span>
        )}
      </div>
      <ol className="flex items-start gap-1 overflow-x-auto -mx-1 px-1 pb-1">
        {steps.map((s, i) => {
          const state =
            activeIdx < 0
              ? "next"
              : i < activeIdx
                ? "done"
                : i === activeIdx
                  ? "now"
                  : "next";
          return (
            <li
              key={s.label}
              className="flex items-start flex-shrink-0 min-w-0"
            >
              <div className="flex flex-col items-center gap-1.5 px-1.5">
                <div
                  className={cx(
                    "w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-colors",
                    state === "done" &&
                      "bg-status-approved-fg text-white",
                    state === "now" &&
                      "bg-brand-700 text-white ring-4 ring-brand-100",
                    state === "next" &&
                      "bg-sand-100 text-ink-500 border border-sand-200",
                  )}
                >
                  {state === "done" ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div className="flex flex-col items-center text-center min-w-[80px]">
                  <span
                    className={cx(
                      "text-[11.5px] font-medium leading-tight",
                      state === "now" && "text-brand-800",
                      state === "done" && "text-ink-700",
                      state === "next" && "text-ink-500",
                    )}
                  >
                    {s.label}
                  </span>
                  {s.hint && (
                    <span className="text-[10px] text-ink-400 mt-0.5">
                      {s.hint}
                    </span>
                  )}
                </div>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={cx(
                    "h-0.5 mt-3.5 flex-1 min-w-[16px] sm:min-w-[28px]",
                    state === "done" || (state === "now" && i < activeIdx)
                      ? "bg-status-approved-fg"
                      : "bg-sand-100",
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
      <ForecastHint forecast={detail.timelineForecast} />
    </Card>
  );
}

function ForecastHint({
  forecast,
}: {
  forecast: ProjectDetail["timelineForecast"];
}) {
  if (forecast.sampleSize < 3) {
    return (
      <p className="text-[11.5px] text-ink-400 leading-snug mt-3">
        Forecast: not enough history yet to project. The hints above are
        building-wide rule-of-thumb averages.
      </p>
    );
  }
  return (
    <p className="text-[11.5px] text-ink-500 leading-snug mt-3">
      <span className="font-medium text-ink-700">
        Typical timing in this building:
      </span>{" "}
      {forecast.medianDays} days median submit-to-decision over{" "}
      {forecast.sampleSize} comparable project
      {forecast.sampleSize === 1 ? "" : "s"} in the last{" "}
      {Math.round(forecast.windowDays / 30)} months
      {forecast.p90Days != null && (
        <>
          {" "}
          (90th percentile: {forecast.p90Days} days, so plan for outliers)
        </>
      )}
      .
    </p>
  );
}

// ---------------------------------------------------------------------------
// Status-driven "what to do next" banner. One sentence + one CTA,
// rendered at the very top of the project page so the active party
// (owner, PM, board member) sees what's blocking the project from
// moving without having to read the page. Returns null on closed
// projects where there's nothing to do.
// ---------------------------------------------------------------------------
type NextAction = {
  tone: "now" | "wait" | "done" | "blocked";
  icon: React.ReactNode;
  title: string;
  body: string;
  cta?: { label: string; href: string };
};

function nextActionFor({
  detail,
  isOwnerOfUnit,
  isStaff,
  isBoard,
  isRecusedHere,
}: {
  detail: ProjectDetail;
  isOwnerOfUnit: boolean;
  isStaff: boolean;
  isBoard: boolean;
  isRecusedHere: boolean;
}): NextAction | null {
  const status = detail.statusCode;
  const project = detail.data;
  const ready = detail.readiness.ready;
  const missing = detail.readiness.missing.length;
  const voting = detail.voting;
  const projectPath = `/projects/${project.id}`;

  const daysIn = (() => {
    if (!project.submittedAt) return 0;
    return Math.floor(
      (Date.now() - new Date(project.submittedAt).getTime()) / 86_400_000,
    );
  })();

  // -------- Draft / editable states (owner first, staff second) -----------
  if (status === "draft") {
    if (isOwnerOfUnit) {
      return ready
        ? {
            tone: "now",
            icon: <CheckCircle2 size={20} />,
            title: "Ready to submit",
            body: "Every required item is in place. Submit to send this to Property Management for review.",
            cta: { label: "Submit for review", href: `${projectPath}#submit` },
          }
        : {
            tone: "blocked",
            icon: <Hourglass size={20} />,
            title: `Draft — ${missing} item${missing === 1 ? "" : "s"} still missing`,
            body: "Finish the required documents and acknowledgments below to unlock submission.",
            cta: {
              label: "See what's missing",
              href: `${projectPath}#section-requirements`,
            },
          };
    }
    return {
      tone: "wait",
      icon: <Clock size={20} />,
      title: "Draft — owner is preparing",
      body: "The unit owner is still finalizing the application. Nothing for staff to do yet.",
    };
  }

  if (status === "pending_owner_revisions" || status === "resubmission_required") {
    if (isOwnerOfUnit) {
      return {
        tone: "blocked",
        icon: <Pencil size={20} />,
        title: "Property Management asked for revisions",
        body: "Read the comments below, edit your draft, and resubmit when you're ready.",
        cta: { label: "Edit draft", href: `${projectPath}?edit=1` },
      };
    }
    return {
      tone: "wait",
      icon: <Clock size={20} />,
      title: "Awaiting owner revisions",
      body: "The owner needs to address the comments left during review.",
    };
  }

  if (status === "submitted") {
    if (isStaff) {
      return {
        tone: "now",
        icon: <ArrowRight size={20} />,
        title: "Awaiting Management review",
        body: `Submitted ${daysIn === 0 ? "today" : `${daysIn} day${daysIn === 1 ? "" : "s"} ago`}. Open the Management queue to start review.`,
        cta: { label: "Start review", href: "/management" },
      };
    }
    if (isOwnerOfUnit) {
      return {
        tone: "wait",
        icon: <Hourglass size={20} />,
        title: "Property Management is reviewing",
        body: `Submitted ${daysIn === 0 ? "today" : `${daysIn} day${daysIn === 1 ? "" : "s"} ago`}. Reviews typically wrap in 3 days. We'll notify you when there's an update.`,
      };
    }
  }

  if (status === "under_management_review") {
    if (isStaff) {
      return {
        tone: "now",
        icon: <ArrowRight size={20} />,
        title: "You're reviewing this project",
        body: "Use Management actions below to route to Board, return for revisions, or approve administratively.",
        cta: { label: "Jump to actions", href: `${projectPath}#section-staff` },
      };
    }
    return {
      tone: "wait",
      icon: <Hourglass size={20} />,
      title: "Property Management is reviewing",
      body: "No action needed from you right now.",
    };
  }

  if (status === "under_board_review") {
    const eligible = voting.eligibleVoters ?? 0;
    const cast = voting.votesCast ?? 0;
    const closes = voting.votingWindowEndsAt
      ? new Date(voting.votingWindowEndsAt as unknown as string)
      : null;
    const daysLeft = closes
      ? Math.ceil((closes.getTime() - Date.now()) / 86_400_000)
      : null;
    if (isBoard && !isRecusedHere) {
      return {
        tone: "now",
        icon: <VoteIcon size={20} />,
        title: "Cast your Board vote",
        body: `${cast} of ${eligible} votes cast.${daysLeft != null ? ` Window closes in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.` : ""}`,
        cta: { label: "Open ballot", href: "/board" },
      };
    }
    if (isStaff) {
      return {
        tone: "wait",
        icon: <Hourglass size={20} />,
        title: "Board is voting",
        body: `${cast} of ${eligible} votes cast.${daysLeft != null ? ` Window closes in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.` : ""}`,
      };
    }
    if (isOwnerOfUnit) {
      return {
        tone: "wait",
        icon: <Hourglass size={20} />,
        title: "Board is voting",
        body: `${cast} of ${eligible} Board members have cast their vote${daysLeft != null ? `; window closes in ${daysLeft} day${daysLeft === 1 ? "" : "s"}` : ""}. We'll notify you when the decision is finalized.`,
      };
    }
  }

  if (
    status === "approved" ||
    status === "conditionally_approved"
  ) {
    if (isOwnerOfUnit) {
      return {
        tone: "now",
        icon: <CheckCircle2 size={20} />,
        title:
          status === "conditionally_approved"
            ? "Conditionally approved"
            : "Approved",
        body:
          status === "conditionally_approved"
            ? "Read the conditions in comments before scheduling. Reach out to Management with questions."
            : "You're cleared to schedule the work. Coordinate start dates with your contractors.",
      };
    }
  }

  if (status === "rejected") {
    if (isOwnerOfUnit) {
      return {
        tone: "blocked",
        icon: <Lock size={20} />,
        title: "Project was rejected",
        body: "Read the reason in comments. You can start a new project draft addressing the issues raised.",
        cta: { label: "See comments", href: `${projectPath}#section-comments` },
      };
    }
  }

  if (status === "scheduled") {
    return {
      tone: "wait",
      icon: <Clock size={20} />,
      title: "Scheduled",
      body: "Work is scheduled. Coordinate with your contractors and Property Management on start day.",
    };
  }

  if (status === "in_progress") {
    if (isOwnerOfUnit) {
      return {
        tone: "now",
        icon: <Sparkles size={20} />,
        title: "Work in progress",
        body: "Phased Chief Engineer inspections must sign off before each next phase. Don't skip any.",
      };
    }
  }

  if (status === "inspection_required") {
    return {
      tone: "now",
      icon: <Sparkles size={20} />,
      title: "Inspection required",
      body: "Chief Engineer needs to sign off the current phase before work continues.",
    };
  }

  if (status === "completed" || status === "closed") {
    return {
      tone: "done",
      icon: <CheckCircle2 size={20} />,
      title: status === "closed" ? "Closed" : "Completed",
      body:
        status === "closed"
          ? "This project is closed. The deposit has been refunded or withheld per closeout."
          : "Work is complete. Closeout (deposit refund + after-photos) is pending.",
    };
  }

  return null;
}

function NextActionBanner(props: {
  detail: ProjectDetail;
  isOwnerOfUnit: boolean;
  isStaff: boolean;
  isBoard: boolean;
  isRecusedHere: boolean;
}) {
  const action = nextActionFor(props);
  if (!action) return null;

  const tone = action.tone;
  const wrapClass = cx(
    "rounded-xl border p-4 sm:p-5 mb-4 flex items-start gap-3 sm:gap-4",
    tone === "now" && "bg-brand-50 border-brand-200",
    tone === "blocked" && "bg-status-rejected-bg border-status-rejected-fg/30",
    tone === "wait" && "bg-status-review-bg border-status-review-fg/30",
    tone === "done" && "bg-status-approved-bg border-status-approved-fg/30",
  );
  const iconClass = cx(
    "w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0",
    tone === "now" && "bg-brand-700 text-white",
    tone === "blocked" && "bg-status-rejected-fg text-white",
    tone === "wait" && "bg-status-review-fg text-white",
    tone === "done" && "bg-status-approved-fg text-white",
  );

  return (
    <div className={wrapClass}>
      <div className={iconClass}>{action.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-ink-800 font-semibold text-[14.5px]">
          {action.title}
        </div>
        <div className="text-ink-700 text-[13px] mt-0.5 leading-relaxed">
          {action.body}
        </div>
      </div>
      {action.cta && (
        <a
          href={action.cta.href}
          className={cx(
            "inline-flex items-center gap-1 px-3.5 py-2 rounded-md text-[13px] font-medium no-underline hover:no-underline whitespace-nowrap flex-shrink-0",
            tone === "now"
              ? "bg-brand-700 text-white hover:bg-brand-800"
              : "bg-white text-ink-700 border border-sand-200 hover:bg-sand-50",
          )}
        >
          {action.cta.label}
          <ArrowRight size={14} />
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function RequirementsCard({
  detail,
  projectId,
  onReload,
}: {
  detail: ProjectDetail;
  projectId: string;
  onReload: () => void | Promise<void>;
}) {
  const [docTypeCode, setDocTypeCode] = useState("");
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [suggestion, setSuggestion] = useState<{
    code: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ownerReqs = detail.requirements.filter(
    (r) => r.requiredFrom === "owner",
  );
  // Doc types the owner satisfies somewhere else (click-wrap, scope
  // textarea, /my/insurance) — never via the file-upload dropdown.
  // Filtering them out here matches what the requirement pills above
  // already do (linking those rows to their real surface) and stops
  // owners from uploading e.g. their indemnity as a PDF and wondering
  // why it doesn't tick off.
  const ownerUploadCandidates = ownerReqs.filter(
    (r) => !UPLOAD_EXCLUDED_DOC_CODES.has(r.documentTypeCode),
  );
  const otherReqs = detail.requirements.filter(
    (r) => r.requiredFrom !== "owner",
  );

  // When the owner picks a file, immediately ask Elm to classify it
  // and pre-fill the document-type dropdown with the suggestion. The
  // owner can override before clicking Upload. PDFs and images get
  // classified; other types fall through to the empty dropdown.
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setPickedFile(file);
    setSuggestion(null);
    if (!file) return;
    const supported =
      file.type === "application/pdf" || file.type.startsWith("image/");
    if (!supported) return;
    setClassifying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `/api/projects/${projectId}/documents/classify`,
        { method: "POST", credentials: "include", body: fd },
      );
      if (!res.ok) return;
      const body = (await res.json()) as {
        data: {
          documentTypeCode: string;
          confidence: "high" | "medium" | "low";
          reasoning: string;
        } | null;
      };
      if (body.data) {
        setSuggestion({
          code: body.data.documentTypeCode,
          confidence: body.data.confidence,
          reasoning: body.data.reasoning,
        });
        setDocTypeCode(body.data.documentTypeCode);
      }
    } catch {
      // Silent — classifier is best-effort.
    } finally {
      setClassifying(false);
    }
  };

  const upload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    if (!pickedFile || !docTypeCode) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", pickedFile);
    fd.append("documentTypeCode", docTypeCode);
    try {
      const res = await fetch(`/api/projects/${projectId}/documents`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `upload failed (${res.status})`);
      }
      form.reset();
      setDocTypeCode("");
      setPickedFile(null);
      setSuggestion(null);
      await onReload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-ink-800 m-0">Requirements</h2>
        {!detail.readiness.ready ? (
          <span className="text-sm text-status-revisions-fg font-medium">
            {detail.readiness.missing.length} required item
            {detail.readiness.missing.length === 1 ? "" : "s"} missing
          </span>
        ) : (
          <span className="text-sm text-status-approved-fg font-medium inline-flex items-center gap-1">
            <CheckCircle2 size={16} /> Ready to submit
          </span>
        )}
      </div>

      <div className="mb-4">
        <h3 className="text-ink-800 mb-2 text-sm uppercase tracking-wider text-ink-500">
          Owner
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {ownerReqs.map((r) => {
            const dest = destinationForReq(r.documentTypeCode, r.requiredFrom);
            return (
              <ReqPill
                key={r.requirementId}
                fulfilled={r.fulfilled}
                href={dest?.href}
                external={dest?.external}
              >
                {r.documentTypeName}
              </ReqPill>
            );
          })}
        </div>
      </div>

      {otherReqs.length > 0 && (
        <div className="mb-4">
          <h3 className="text-ink-800 mb-2 text-sm uppercase tracking-wider text-ink-500">
            Contractor team
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {otherReqs.map((r) => (
              <ReqPill
                key={r.requirementId}
                fulfilled={r.fulfilled}
                tag={r.requiredFrom}
              >
                {r.documentTypeName}
              </ReqPill>
            ))}
          </div>
        </div>
      )}

      <hr className="border-ink-100 my-4" />

      <form onSubmit={upload}>
        <h3 className="text-ink-800 mb-2">Upload owner document</h3>
        <div className="field-row">
          <Field label="File (≤25MB)">
            <input
              className="input"
              type="file"
              name="file"
              required
              onChange={onFileChange}
              accept="application/pdf,image/*"
            />
          </Field>
          <Field
            label="Document type"
            hint={
              classifying
                ? "Elm is reading the file…"
                : suggestion
                  ? `Elm suggested: ${suggestion.reasoning}`
                  : "Pick a type, or attach a PDF/image and Elm will suggest one."
            }
          >
            <select
              className="select"
              value={docTypeCode}
              onChange={(e) => setDocTypeCode(e.target.value)}
              required
              disabled={classifying}
            >
              <option value="">Choose…</option>
              {ownerUploadCandidates.map((r) => (
                <option key={r.documentTypeCode} value={r.documentTypeCode}>
                  {r.documentTypeName}
                  {r.fulfilled ? " (uploaded)" : ""}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {suggestion && docTypeCode === suggestion.code && (
          <div className="text-[12px] text-ink-500 -mt-1 mb-3">
            <span
              className={cx(
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold mr-2",
                suggestion.confidence === "high"
                  ? "bg-status-approved-bg text-status-approved-fg"
                  : suggestion.confidence === "medium"
                    ? "bg-status-review-bg text-status-review-fg"
                    : "bg-sand-100 text-ink-600",
              )}
            >
              Elm · {suggestion.confidence}
            </span>
            Auto-classified. Change the dropdown if it's wrong.
          </div>
        )}
        {error && <div className="alert-error">{error}</div>}
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!docTypeCode || !pickedFile || classifying}
        >
          <Upload size={16} />
          {busy ? "Uploading…" : "Upload"}
        </Button>
      </form>
    </Card>
  );
}

// Map an owner-side requirement code to the page section / route that
// the owner can use to satisfy it. Returns null when the requirement
// is just a document upload — those continue to live in the upload
// form below.
function destinationForReq(
  code: string,
  party: string,
): { href: string; external?: boolean } | null {
  if (party !== "owner") return null;
  switch (code) {
    case "scope_of_work":
      return { href: "#section-scope" };
    case "indemnity_agreement":
      return { href: "#section-indemnity" };
    case "rules_acknowledgement":
      return { href: "#section-rules-ack" };
    case "procedures_acknowledgement":
      return { href: "#section-procedures-ack" };
    case "owner_liability_coi":
      return { href: "/my/insurance", external: true };
    case "riser_shutdown_request":
      return { href: "#section-riser-shutdown" };
    default:
      return null;
  }
}

function ReqPill({
  fulfilled,
  children,
  tag,
  href,
  external,
}: {
  fulfilled: boolean;
  children: React.ReactNode;
  tag?: string;
  href?: string;
  external?: boolean;
}) {
  const className = cx(
    "rounded-xl px-3 py-2 text-sm flex items-center gap-2 border transition-colors",
    fulfilled
      ? "bg-status-approved-bg border-status-approved-fg/30 text-status-approved-fg"
      : "bg-status-revisions-bg border-status-revisions-fg/30 text-status-revisions-fg",
    href && "hover:shadow-soft hover:-translate-y-px no-underline",
  );
  const inner = (
    <>
      {fulfilled ? (
        <CheckCircle2 size={16} className="flex-shrink-0" />
      ) : (
        <Circle size={16} className="flex-shrink-0" />
      )}
      <span className="flex-1 min-w-0 truncate">
        {tag && (
          <span className="font-mono text-[11px] text-ink-500 mr-1">
            [{tag}]
          </span>
        )}
        {children}
      </span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className={className}
      >
        {inner}
      </a>
    );
  }
  return <div className={className}>{inner}</div>;
}

// ---------------------------------------------------------------------------
function VotingCard({
  voting,
  summary,
  isStaff,
  isBoard,
  isRecusedHere,
  projectId,
  onChange,
}: {
  voting: BoardVotingDetail | null;
  summary: ProjectDetail["voting"];
  isStaff: boolean;
  isBoard: boolean;
  isRecusedHere: boolean;
  projectId: string;
  onChange: () => void | Promise<void>;
}) {
  const [vote, setVote] = useState("approve");
  const [conditions, setConditions] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body: Record<string, unknown> = { vote };
      if (vote === "conditionally_approve") body["conditions"] = conditions;
      if (
        vote === "reject" ||
        vote === "request_revisions" ||
        vote === "recuse"
      ) {
        body["reason"] = reason;
      }
      await api.post(`/api/projects/${projectId}/board/votes`, body);
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : "Vote failed",
      );
    } finally {
      setBusy(false);
    }
  };

  if (summary.state === "no_board_step") {
    return (
      <Card>
        <h2 className="text-ink-800 m-0 mb-2">Board review</h2>
        <p className="text-ink-500 text-sm">
          No Board review has been opened yet.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
          <VoteIcon size={18} /> Board review
        </h2>
        {summary.decision && <StatusBadge status={summary.decision} />}
      </div>
      <p className="text-ink-700">
        <span className="font-semibold">{summary.votesCast ?? 0}</span> of{" "}
        <span className="font-semibold">{summary.eligibleVoters ?? 0}</span>{" "}
        votes received
        {summary.votingWindowEndsAt && (
          <span className="text-ink-500">
            {" · window closes "}
            {new Date(summary.votingWindowEndsAt).toLocaleDateString()}
          </span>
        )}
      </p>

      {isStaff && voting?.tally && (
        <div className="mt-4">
          <h3 className="text-ink-800 mb-2">Tally</h3>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(voting.tally).map(([k, v]) => (
              <span
                key={k}
                className="px-2.5 py-1 rounded-full bg-ink-50 border border-ink-200 text-xs"
              >
                {k.replaceAll("_", " ")}:{" "}
                <span className="font-semibold text-ink-800">{v}</span>
              </span>
            ))}
          </div>
          <h3 className="text-ink-800 mt-4 mb-2">Per voter</h3>
          <ul className="divide-y divide-ink-100">
            {(voting.votes ?? []).map((v) => (
              <li
                key={v.voterUserId}
                className="py-2 flex items-center gap-3 text-sm"
              >
                <code className="text-xs">{v.voterUserId.slice(0, 8)}…</code>
                <StatusBadge status={v.vote} />
                {v.conditions && (
                  <span className="text-ink-500 truncate">{v.conditions}</span>
                )}
                {v.reason && (
                  <span className="text-ink-500 truncate">{v.reason}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isBoard && isRecusedHere && (
        <div className="alert-warning mt-4">
          You are <strong>auto-recused</strong> from voting on this
          project because you are a current owner of the unit. Per the
          building's rules, Board Members cannot act as a Board Member
          on their own unit. Your Board Member role is paused for this
          project only — your owner privileges (signing the indemnity,
          requesting riser shutdowns, submitting for review) are
          unaffected.
        </div>
      )}
      {isBoard && !isRecusedHere && (
        <div className="mt-4">
          {voting?.myVote && (
            <div className="alert-info">
              Your current vote:{" "}
              <strong>{voting.myVote.vote.replaceAll("_", " ")}</strong>
            </div>
          )}
          {!voting?.finalized && (
            <form onSubmit={submit}>
              <h3 className="text-ink-800 mb-2">
                {voting?.myVote ? "Change your vote" : "Cast your vote"}
              </h3>
              <Field label="Vote">
                <select
                  className="select"
                  value={vote}
                  onChange={(e) => setVote(e.target.value)}
                >
                  {VOTE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              {vote === "conditionally_approve" && (
                <Field label="Conditions (required)">
                  <textarea
                    className="textarea"
                    rows={3}
                    value={conditions}
                    onChange={(e) => setConditions(e.target.value)}
                    required
                  />
                </Field>
              )}
              {(vote === "reject" ||
                vote === "request_revisions" ||
                vote === "recuse") && (
                <Field label="Reason (required)">
                  <textarea
                    className="textarea"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                  />
                </Field>
              )}
              {error && <div className="alert-error">{error}</div>}
              <Button type="submit" variant="primary" loading={busy}>
                Submit vote
              </Button>
            </form>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
async function markCommentsRead(
  projectId: string,
  reload: () => void | Promise<void>,
) {
  try {
    await api.post("/api/notifications/read-all", { projectId });
    await reload();
  } catch {
    // Best-effort — failing to mark read shouldn't block anything.
  }
}

// ---------------------------------------------------------------------------
// Elm-summarized rollup of long comment threads. Renders inline at
// the top of the Comments card once the thread reaches 20 visible
// posts. Quietly hides when the LLM summarization isn't available
// (no key / network) — the raw thread below is always the canonical
// view.
// ---------------------------------------------------------------------------
function CommentThreadSummary({
  projectId,
  commentCount,
}: {
  projectId: string;
  commentCount: number;
}) {
  const [data, setData] = useState<CommentSummaryEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<CommentSummaryEnvelope>(`/api/projects/${projectId}/comments/summary`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-fetch when count changes meaningfully (every new post).
  }, [projectId, commentCount]);

  if (loading) {
    return (
      <div className="mb-3 px-3.5 py-2.5 bg-brand-50 border border-brand-100 rounded-md">
        <p className="text-[12.5px] text-ink-500 inline-flex items-center gap-2">
          <Sparkles size={13} className="text-brand-700" />
          Elm is summarizing the thread…
        </p>
      </div>
    );
  }
  if (!data?.data) return null;

  const s = data.data;
  const PARTY_LABEL: Record<typeof s.outstandingFor.party, string> = {
    owner: "Owner",
    staff: "PM",
    board: "Board",
    contractor: "Contractor",
    nobody: "Nobody",
  };

  return (
    <div className="mb-4 rounded-md border border-brand-100 bg-brand-50/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-brand-50 transition-colors"
      >
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Sparkles
            size={13}
            className="text-brand-700 mt-0.5 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-wider text-brand-700 font-semibold mb-0.5">
              Elm thread summary · {data.commentCount} comments
            </div>
            <div className="text-[13px] text-ink-800 font-medium leading-snug">
              {s.headline}
            </div>
          </div>
        </div>
        <span className="text-[11.5px] text-brand-700 flex-shrink-0 mt-1">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open && (
        <div className="px-3.5 pb-3 pt-1 border-t border-brand-100 flex flex-col gap-2.5">
          {s.digest && (
            <p className="text-[12.5px] text-ink-700 leading-snug m-0">
              {s.digest}
            </p>
          )}
          {s.topics.length > 0 && (
            <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
              {s.topics.map((t, i) => (
                <li
                  key={i}
                  className="text-[12.5px] text-ink-700 flex items-start gap-2"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-700 mt-1.5 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <strong className="text-ink-800">{t.topic}:</strong>{" "}
                    {t.status}{" "}
                    {t.awaitingFrom !== "nobody" && (
                      <span className="text-[11px] text-ink-500">
                        (awaiting {t.awaitingFrom})
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {s.outstandingFor.items.length > 0 && (
            <div className="rounded-md bg-white border border-brand-100 p-2.5">
              <div className="text-[10.5px] uppercase tracking-wider text-ink-500 font-semibold mb-1 inline-flex items-center gap-1">
                <ListChecks size={11} />
                Outstanding for {PARTY_LABEL[s.outstandingFor.party]}
              </div>
              <ul className="flex flex-col gap-1 m-0 p-0 list-none">
                {s.outstandingFor.items.map((item, i) => (
                  <li
                    key={i}
                    className="text-[12.5px] text-ink-700 flex items-start gap-2"
                  >
                    <Circle size={6} className="text-ink-400 mt-1.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[10.5px] text-ink-400 leading-snug m-0">
            Summary is advisory — read the full thread below if anything looks
            off.
          </p>
        </div>
      )}
    </div>
  );
}

function CommentsCard({
  projectId,
  comments,
  canPostInternal,
  unreadCount,
  onMarkRead,
  importCandidates,
  onChange,
}: {
  projectId: string;
  comments: CommentRow[] | null;
  canPostInternal: boolean;
  unreadCount?: number;
  onMarkRead?: () => void;
  /** Owner-side document-type candidates for the URL-import action
   *  on PDF / image previews. Empty when the viewer can't import
   *  (anyone other than the unit owner). */
  importCandidates?: Requirement[];
  onChange: () => void | Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState<"all" | "internal">("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${projectId}/comments`, {
        body,
        visibility,
      });
      setBody("");
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : "Post failed",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
          <MessageCircle size={18} /> Comments
          {unreadCount && unreadCount > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold bg-status-rejected-bg text-status-rejected-fg">
              {unreadCount} new
            </span>
          ) : null}
        </h2>
        {unreadCount && unreadCount > 0 && onMarkRead ? (
          <button
            type="button"
            onClick={onMarkRead}
            className="text-[12px] text-brand-700 hover:text-brand-800 px-2.5 py-1 rounded-md hover:bg-brand-50"
          >
            Mark read
          </button>
        ) : null}
      </div>
      {comments && comments.length >= 20 && (
        <CommentThreadSummary projectId={projectId} commentCount={comments.length} />
      )}
      {comments === null ? (
        <p className="text-ink-500">Loading…</p>
      ) : comments.length === 0 ? (
        <p className="text-ink-500 text-sm">No comments yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.map((c) => (
            <li
              key={c.id}
              className={cx(
                "rounded-xl border p-3",
                c.visibility === "internal"
                  ? "bg-ink-50 border-ink-200 border-dashed"
                  : "bg-white border-ink-200",
              )}
            >
              <div className="flex items-baseline gap-2 mb-1">
                <strong className="text-ink-800 text-sm">
                  {c.authorUserId.slice(0, 8)}…
                </strong>
                <span className="text-xs text-ink-500">
                  · {new Date(c.createdAt).toLocaleString()}
                </span>
                {c.visibility === "internal" && (
                  <span className="badge bg-ink-100 text-ink-600 inline-flex items-center gap-1">
                    <Lock size={10} /> internal
                  </span>
                )}
              </div>
              <div className="text-ink-700 whitespace-pre-wrap text-sm">
                {c.body}
              </div>
              {extractUrls(c.body).map((u) => (
                <LinkPreviewCard
                  key={u}
                  url={u}
                  projectId={projectId}
                  importCandidates={importCandidates}
                  onImported={onChange}
                />
              ))}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-4">
        <Field>
          <textarea
            className="textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a comment…"
            rows={3}
            required
          />
        </Field>
        {canPostInternal && (
          <label className="flex items-center gap-2 text-sm text-ink-700 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={visibility === "internal"}
              onChange={(e) =>
                setVisibility(e.target.checked ? "internal" : "all")
              }
              className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
            />
            Staff-internal (hidden from owner)
          </label>
        )}
        {error && <div className="alert-error">{error}</div>}
        <Button type="submit" variant="primary" loading={busy} disabled={!body}>
          <Send size={14} />
          Post comment
        </Button>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Editable scope/title/impact card for owners on draft-equivalent
// statuses (draft, pending_owner_revisions, resubmission_required).
// Patches /api/projects/:id, then reloads.
// ---------------------------------------------------------------------------
function EditDraftCard({
  detail,
  onChange,
  startInEdit = false,
}: {
  detail: ProjectDetail;
  onChange: () => void | Promise<void>;
  /** When true, the card opens directly in edit mode — used by the
   *  Edit action on /projects so the owner lands in the form, not
   *  the read-only view. */
  startInEdit?: boolean;
}) {
  const project = detail.data;
  const [editing, setEditing] = useState(startInEdit);
  const [title, setTitle] = useState(project.title);
  const [scopeOfWork, setScopeOfWork] = useState(project.scopeOfWork);
  const [requiresRiserShutdown, setRiser] = useState(
    project.requiresRiserShutdown,
  );
  const [requiresTelecomShutdown, setTelecom] = useState(
    project.requiresTelecomShutdown,
  );
  const [requiresCityPermit, setCityPermit] = useState(
    project.requiresCityPermit ?? false,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state when the underlying record changes (e.g. after a
  // successful save reloads the detail).
  useEffect(() => {
    if (!editing) {
      setTitle(project.title);
      setScopeOfWork(project.scopeOfWork);
      setRiser(project.requiresRiserShutdown);
      setTelecom(project.requiresTelecomShutdown);
      setCityPermit(project.requiresCityPermit ?? false);
    }
  }, [
    editing,
    project.title,
    project.scopeOfWork,
    project.requiresRiserShutdown,
    project.requiresTelecomShutdown,
    project.requiresCityPermit,
  ]);

  const cancel = () => {
    setEditing(false);
    setError(null);
    setTitle(project.title);
    setScopeOfWork(project.scopeOfWork);
    setRiser(project.requiresRiserShutdown);
    setTelecom(project.requiresTelecomShutdown);
    setCityPermit(project.requiresCityPermit ?? false);
  };

  const save = async () => {
    if (title.trim().length < 3) {
      setError("Title needs to be at least 3 characters.");
      return;
    }
    if (scopeOfWork.trim().length < 10) {
      setError("Scope of work needs to be at least 10 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/projects/${project.id}`, {
        title: title.trim(),
        scopeOfWork,
        requiresRiserShutdown,
        requiresTelecomShutdown,
        requiresCityPermit,
      });
      setEditing(false);
      await onChange();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const STATUS_LABEL: Record<string, string> = {
    draft: "Draft",
    pending_owner_revisions: "Pending owner revisions",
    resubmission_required: "Resubmission required",
  };
  const statusLabel = STATUS_LABEL[detail.statusCode] ?? "Editable";

  if (!editing) {
    return (
      <Card>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="text-ink-800 m-0 mb-1">Scope</h3>
            <span className="badge bg-status-revisions-bg text-status-revisions-fg">
              {statusLabel}
            </span>
          </div>
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            <Pencil size={14} />
            Edit
          </Button>
        </div>
        <p className="whitespace-pre-wrap text-ink-700">
          {project.scopeOfWork}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-ink-800 m-0">Edit draft</h3>
        <span className="badge bg-status-revisions-bg text-status-revisions-fg">
          {statusLabel}
        </span>
      </div>
      <Field label="Title">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          minLength={3}
          required
        />
      </Field>
      <Field
        label="Scope of work"
        hint="Name every room, fixture, and material affected. Vague scopes cause revision requests."
      >
        <textarea
          className="textarea min-h-[220px] font-mono text-[12.5px] leading-relaxed"
          value={scopeOfWork}
          onChange={(e) => setScopeOfWork(e.target.value)}
          rows={10}
          minLength={10}
          required
        />
      </Field>
      <ScopeRefineButton
        scopeOfWork={scopeOfWork}
        projectTypeIds={detail.projectTypes.map((t) => t.id)}
        unitId={project.unitId}
        flags={{
          requiresRiserShutdown,
          requiresTelecomShutdown,
          isEmergency: project.isEmergency,
        }}
        onAccept={(refined) => setScopeOfWork(refined)}
      />
      <div className="flex flex-col gap-2 mt-3">
        <label className="flex items-start gap-2 text-[13px] text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={requiresRiserShutdown}
            onChange={(e) => setRiser(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Requires water riser shutdown
            <span className="block text-[11.5px] text-ink-500">
              72-hour notice; $150 fee.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-[13px] text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={requiresTelecomShutdown}
            onChange={(e) => setTelecom(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Telecom / cable / antenna shutdown
            <span className="block text-[11.5px] text-ink-500">
              First per system free, then $100 each. 10 AM – 3 PM Mon–Fri only.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-[13px] text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={requiresCityPermit}
            onChange={(e) => setCityPermit(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Requires City of Chicago permit
            <span className="block text-[11.5px] text-ink-500">
              Only for scopes that trigger Department of Buildings review
              (structural, electrical service, plumbing relocation). Cosmetic
              refreshes typically don't.
            </span>
          </span>
        </label>
      </div>
      {error && <div className="alert-error mt-3">{error}</div>}
      <div className="flex gap-2 mt-4">
        <Button variant="primary" onClick={() => void save()} loading={busy}>
          Save changes
        </Button>
        <Button variant="ghost" onClick={cancel} disabled={busy}>
          Cancel
        </Button>
      </div>
      <p className="text-[11.5px] text-ink-500 mt-3 leading-snug">
        Other fields (unit, project type, materials, contractors) aren't
        editable after creation. If something fundamental needs to change,
        delete this draft and start a new project.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function OwnerActions({
  detail,
  onChange,
}: {
  detail: ProjectDetail;
  onChange: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${detail.data.id}/submit`);
      await onChange();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as {
          error?: string;
          missing?:
            | { documentTypeName?: string; requiredFrom?: string }[]
            | string[];
        };
        const friendly: Record<string, string> = {
          owner_liability_insurance_missing:
            "Upload your $500K personal liability insurance certificate before submitting.",
          owner_liability_insurance_pending_validation:
            "Your liability insurance is uploaded but still pending Management validation.",
          indemnity_unsigned:
            "The Indemnity Agreement must be signed before submitting.",
          not_submittable:
            "This project is no longer in a draft state and cannot be submitted.",
          status_unknown: "Project status could not be determined.",
          project_type_missing: "Project type is missing.",
          riser_at_capacity:
            "This unit's tier group is at washer/dryer capacity. Please contact Management.",
          unit_already_has_wd:
            "This unit already has an approved washer/dryer on file.",
        };
        if (body.error === "missing_required_documents") {
          const missing = (body.missing ?? []) as {
            documentTypeName?: string;
            requiredFrom?: string;
          }[];
          const names = missing
            .map((m) =>
              m.documentTypeName
                ? `${m.documentTypeName} (${m.requiredFrom ?? "owner"})`
                : null,
            )
            .filter((s): s is string => !!s);
          setError(
            names.length
              ? `Missing ${names.length} required document${names.length === 1 ? "" : "s"}: ${names.join(", ")}.`
              : `Missing required documents.`,
          );
        } else if (body.error === "kitchen_cabinet_details_required") {
          const missing = (body.missing ?? []) as string[];
          setError(
            missing.length
              ? `Cabinet replacement requires: ${missing.join(", ")}. Update the kitchen details on this project and retry.`
              : "Cabinet replacement requires countertop, faucet, and sink details. Update the kitchen details on this project and retry.",
          );
        } else if (body.error && friendly[body.error]) {
          setError(friendly[body.error] ?? body.error);
        } else setError(`Submit failed: ${body.error ?? "unknown error"}`);
      } else setError("Submit failed.");
    } finally {
      setBusy(false);
    }
  };

  if (
    detail.statusCode !== "draft" &&
    detail.statusCode !== "pending_owner_revisions" &&
    detail.statusCode !== "resubmission_required"
  )
    return null;
  if (!detail.readiness.ready && detail.readiness.missing.length === 0)
    return null;

  return (
    <Card>
      <h2 className="text-ink-800 m-0 mb-2">Submit for review</h2>
      <p className="text-ink-500 text-sm mb-3">
        Submission is gated on the owner-side requirements above. Contractor
        items are checked once contractors are invited.
      </p>
      {error && <div className="alert-error">{error}</div>}
      <Button
        variant="accent"
        onClick={() => void submit()}
        loading={busy}
        disabled={!detail.readiness.ready}
      >
        {busy ? "Submitting…" : "Submit for management review"}
      </Button>
    </Card>
  );
}

function StaffActions({
  projectId,
  onChange,
}: {
  projectId: string;
  onChange: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const call = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/management/projects/${projectId}/${path}`, body);
      await onChange();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 mb-2">Management actions</h2>
      <div className="flex flex-wrap gap-2 mb-3">
        <Button onClick={() => void call("start-review")} disabled={busy}>
          Start review
        </Button>
        <Button onClick={() => void call("route-to-board")} disabled={busy}>
          Route to Board
        </Button>
        <Button
          onClick={() => void call("approve-administratively")}
          disabled={busy}
        >
          Approve administratively
        </Button>
      </div>
      <Field label="Reason (required for return)">
        <input
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <Button
        variant="danger"
        onClick={() => void call("return-for-revisions", { reason })}
        disabled={busy || reason.length < 3}
      >
        Return for revisions
      </Button>
      {error && (
        <div className="alert-error mt-3">{error}</div>
      )}
    </Card>
  );
}
